import React, { useCallback, useMemo } from 'react';
import { Link } from '@mui/material';
import { EllipsisWithTooltip } from '../../../components/Shared/Tables/TableV1.tsx';
import { BadgeCell } from '../../../components/ui';
import UserColumnHeader from '../components/UserColumnHeader';
import UserRowActions from '../components/UserRowActions';
import { USER_STATUS_LABELS } from './userStatusCopy';
import useUserSx from '../hooks/useUserSx';

function UserIndexCell({ page, pageSize, rowIndex }) {
  const serial = page * pageSize + rowIndex + 1;
  return (
    <EllipsisWithTooltip
      text={String(serial)}
      maxWidth={56}
      fontSize="0.8125rem"
      fontWeight={500}
    />
  );
}

export function useUserTableColumns({
  page,
  pageSize,
  showSuperuserColumns,
  onEdit,
  onChangePassword,
  onUnlock,
  onDeactivate,
  onActivate,
  onDelete,
  onOrganisationClick,
  onProfileClick,
}) {
  const { actionColumnHeadCell, actionColumnBodyCell } = useUserSx();

  const renderHeader = useCallback(
    (label) => () => <UserColumnHeader>{label}</UserColumnHeader>,
    []
  );

  return useMemo(() => {
    const columns = [
      {
        id: 'index',
        accessorKey: 'index',
        header: 'S.No.',
        Header: renderHeader('S.No.'),
        enableSorting: false,
        size: 72,
        minSize: 64,
        maxSize: 80,
        Cell: ({ row }) => (
          <UserIndexCell page={page} pageSize={pageSize} rowIndex={row.index} />
        ),
      },
      {
        accessorKey: 'first_name',
        header: 'First Name',
        Header: renderHeader('First Name'),
        enableSorting: false,
        size: 160,
        minSize: 140,
        Cell: ({ cell }) => (
          <EllipsisWithTooltip text={cell.getValue()} maxWidth={150} fontSize="0.8125rem" />
        ),
      },
      {
        accessorKey: 'last_name',
        header: 'Last Name',
        Header: renderHeader('Last Name'),
        enableSorting: false,
        size: 160,
        minSize: 140,
        Cell: ({ cell }) => (
          <EllipsisWithTooltip text={cell.getValue()} maxWidth={150} fontSize="0.8125rem" />
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        Header: renderHeader('Email'),
        enableSorting: false,
        size: 220,
        minSize: 180,
        Cell: ({ cell }) => (
          <EllipsisWithTooltip text={cell.getValue()} maxWidth={210} fontSize="0.8125rem" />
        ),
      },
      {
        accessorKey: 'username',
        header: 'Username',
        Header: renderHeader('Username'),
        enableSorting: false,
        size: 160,
        minSize: 140,
        Cell: ({ cell }) => (
          <EllipsisWithTooltip text={cell.getValue()} maxWidth={150} fontSize="0.8125rem" />
        ),
      },
      {
        accessorKey: 'profile.organisation.name',
        header: 'Organisation',
        Header: renderHeader('Organisation'),
        enableSorting: false,
        size: 180,
        minSize: 160,
        Cell: ({ cell }) => (
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={() => onOrganisationClick?.(cell.getValue())}
            sx={{ fontSize: '0.8125rem', fontWeight: 500 }}
          >
            {cell.getValue() || '—'}
          </Link>
        ),
      },
      {
        accessorKey: 'profile.name',
        header: 'Profile',
        Header: renderHeader('Profile'),
        enableSorting: false,
        size: 160,
        minSize: 140,
        Cell: ({ row, cell }) => (
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={() => onProfileClick?.(row.original.profile)}
            sx={{ fontSize: '0.8125rem', fontWeight: 500 }}
          >
            {cell.getValue() || '—'}
          </Link>
        ),
      },
      {
        accessorKey: 'is_active',
        header: 'Status',
        Header: renderHeader('Status'),
        enableSorting: false,
        size: 120,
        minSize: 100,
        maxSize: 140,
        Cell: ({ cell }) => {
          const isActive = cell.getValue() !== false;
          const badge = isActive
            ? { label: USER_STATUS_LABELS.active, variant: 'success' }
            : { label: USER_STATUS_LABELS.inactive, variant: 'neutral' };
          return <BadgeCell label={badge.label} variant={badge.variant} />;
        },
      },
      {
        id: 'actions',
        accessorKey: 'actions',
        header: 'Action',
        Header: renderHeader('Action'),
        enableSorting: false,
        size: 72,
        minSize: 72,
        maxSize: 72,
        grow: false,
        enablePinning: true,
        muiTableHeadCellProps: {
          sx: actionColumnHeadCell,
        },
        muiTableBodyCellProps: {
          sx: actionColumnBodyCell,
        },
        Cell: ({ row }) => (
          <UserRowActions
            user={row.original}
            rowIndex={row.index}
            onEdit={onEdit}
            onChangePassword={onChangePassword}
            onUnlock={onUnlock}
            onDeactivate={onDeactivate}
            onActivate={onActivate}
            onDelete={onDelete}
          />
        ),
      },
    ];

    if (showSuperuserColumns) {
      columns.splice(3, 0, {
        accessorKey: 'is_superuser',
        header: 'Is SuperUser',
        Header: renderHeader('Is SuperUser'),
        enableSorting: false,
        size: 140,
        minSize: 120,
        maxSize: 160,
        Cell: ({ cell }) => {
          const badge = cell.getValue()
            ? { label: 'True', variant: 'success' }
            : { label: 'False', variant: 'neutral' };
          return <BadgeCell label={badge.label} variant={badge.variant} />;
        },
      });
    }

    return columns;
  }, [
    actionColumnBodyCell,
    actionColumnHeadCell,
    onChangePassword,
    onActivate,
    onDeactivate,
    onDelete,
    onEdit,
    onOrganisationClick,
    onProfileClick,
    onUnlock,
    page,
    pageSize,
    renderHeader,
    showSuperuserColumns,
  ]);
}
